import { CommonActions, useFocusEffect } from '@react-navigation/native';
import { Button, HStack, Spinner, VStack } from 'native-base';
import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import ContentBlockRenderer from 'app/components/ContentBlockRenderer';
import CPAlert from 'app/components/CPAlert';
import CustomHeaderBackButton from 'app/components/CustomHeaderBackButton';
import LinkTicketsPrompt from 'app/components/LinkTicketsPrompt';
import Loading from 'app/components/Loading';
import Screen from 'app/components/Screen';
import ShareButton from 'app/components/ShareButton';
import { useGetProductScreenBlocksQuery } from 'app/generated/hygraph';
import useMetaData from 'app/hooks/useMetaData';
import useRootNavigation from 'app/hooks/useRootNavigation';
import { getRouteParam } from 'app/navigation/navigationHelper';
import { ProductStackParamList } from 'app/navigation/ProductStackNavigator';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import ProductProvider, { useProductContext } from 'app/providers/ProductProvider';
import ProductHero from 'app/screens/ProductScreen/ProductHero';
import ProductIntro from 'app/screens/ProductScreen/ProductIntro';
import ProductLogo from 'app/screens/ProductScreen/ProductLogo';
import HygraphService from 'app/services/HygraphService';
import { getUrl } from 'app/services/ProductHelper';
import { tID } from 'app/services/TestHelper';

const { client } = HygraphService();
export const PRODUCT_BLOCK_SPACE = 10;

export type ProductScreenProps = RootScreenProps<ProductStackParamList, 'Product'>;

export default function ProductScreen({ navigation, route }: ProductScreenProps) {
  const productContentKey = getRouteParam(route, 'productId') ?? '';

  useEffect(() => {
    if (!productContentKey) {
      navigation.navigate('Destinations');
    }
  }, [navigation, productContentKey]);

  if (!productContentKey) return null;

  return (
    <ProductProvider productContentKey={productContentKey}>
      <ProductScreenBody navigation={navigation} route={route} />
    </ProductProvider>
  );
}

function ProductScreenBody({ navigation, route }: ProductScreenProps) {
  const { productId: productContentKey } = route.params;
  const shouldReturnToPurchaseOrderDetails =
    route.params.returnTo === 'PurchaseOrderDetails' && !!route.params.entitlementKey;
  const rootNavigation = useRootNavigation();
  const isHandlingBackRef = useRef(false);
  const { top: topInset } = useSafeAreaInsets();
  const { productContent } = useProductContext();
  const { locale, t } = useI18n();
  const {
    data: blocks = [],
    refetch,
    isError,
    isFetching,
  } = useGetProductScreenBlocksQuery(
    client,
    { locale },
    { select: (data) => data.blockGroup?.blocks }
  );

  useMetaData('Product', { productName: productContent?.name });

  useFocusEffect(
    useCallback(() => {
      StatusBar.setBarStyle('light-content');
      if (Platform.OS === 'android') {
        // Needed on devices without edge-to-edge (many BrowserStack phones): otherwise the
        // system bar stays opaque white and the hero cannot draw underneath.
        StatusBar.setTranslucent(true);
        StatusBar.setBackgroundColor('transparent');
      }
      return () => {
        StatusBar.setBarStyle('dark-content');
      };
    }, [])
  );

  const handleBack = useCallback(() => {
    if (shouldReturnToPurchaseOrderDetails) {
      isHandlingBackRef.current = true;
      const state = rootNavigation.getState();
      const previousRoutes = state.routes.slice(0, -1);

      rootNavigation.dispatch(
        CommonActions.reset({
          index: previousRoutes.length,
          routes: [
            ...previousRoutes,
            {
              name: 'PurchasesStack',
              state: {
                index: 1,
                routes: [
                  { name: 'PurchasesList' },
                  {
                    name: 'PurchaseOrderDetails',
                    params: {
                      entitlementKey: route.params.entitlementKey as string,
                    },
                  },
                ],
              },
            } as any,
          ],
        })
      );
      return;
    }

    rootNavigation.navigate('Destinations');
  }, [rootNavigation, route.params.entitlementKey, shouldReturnToPurchaseOrderDetails]);

  useEffect(() => {
    if (!shouldReturnToPurchaseOrderDetails) return undefined;

    return navigation.addListener('beforeRemove', (event) => {
      if (isHandlingBackRef.current) return;

      event.preventDefault();
      handleBack();
    });
  }, [handleBack, navigation, shouldReturnToPurchaseOrderDetails]);

  if (!productContent) return <Loading />;

  return (
    <Screen
      after={<LinkTicketsPrompt productContentKey={productContentKey} />}
      beforeContainer={
        <ProductHero
          beforeGradient={
            <>
              <HStack
                alignItems="center"
                justifyContent="space-between"
                position="absolute"
                pt={`${topInset}px`}
                top="0"
                w="full"
                zIndex={1}
              >
                <CustomHeaderBackButton
                  style={{ height: 48, width: 48 }}
                  testID={tID('BackButton')}
                  tintColor="white"
                  onPress={handleBack}
                />

                {Platform.OS !== 'web' && (
                  <ShareButton
                    accessibilityLabel={`Share ${productContent.name}`}
                    content={{
                      url: getUrl(productContent, locale),
                      title: productContent.name ?? '',
                    }}
                  />
                )}
              </HStack>
              <ProductLogo mb="12" mt="20" />
            </>
          }
          mb="8"
        >
          <ProductIntro />
        </ProductHero>
      }
    >
      <VStack space={PRODUCT_BLOCK_SPACE}>
        {/* Show spinner while fetching (including during retries) */}
        {isFetching && !blocks.length && <Spinner size="lg" />}
        {/* Show error only after retries are exhausted (not fetching anymore) */}
        {isError && !isFetching && !blocks.length && (
          <VStack space="3">
            <CPAlert status="error">{t('app_error_fetching_data')}</CPAlert>
            <Button isLoading={isFetching} onPress={() => refetch()}>
              {t('gen_try_again')}
            </Button>
          </VStack>
        )}
        {blocks.map((block) => (
          <ContentBlockRenderer key={block.id} space={PRODUCT_BLOCK_SPACE} {...block} />
        ))}
      </VStack>

      {__DEV__ && (
        <Button my="16" onPress={() => refetch()}>
          Refresh Content Blocks
        </Button>
      )}
    </Screen>
  );
}

ProductScreen.routeName = 'Home';
